import { useState } from 'react';
import ScrollReveal from '@/components/base/ScrollReveal';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: '¿Qué requisitos necesito para solicitar un crédito personal?',
    answer: 'Para solicitar un crédito personal necesitas: identificación oficial vigente (INE), comprobante de domicilio reciente (no mayor a 3 meses), comprobantes de ingresos de los últimos 3 meses, y estado de cuenta bancario.',
  },
  {
    question: '¿Cuánto tiempo tarda la aprobación y entrega del crédito?',
    answer: 'El tiempo promedio de respuesta es de 48 horas hábiles una vez que entregas toda tu documentación completa. En muchos casos, la aprobación puede ser el mismo día. El depósito se realiza directamente a tu cuenta bancaria una vez firmado el contrato.',
  },
  {
    question: '¿Qué es una SOFOM y cómo me protege?',
    answer: 'Una SOFOM (Sociedad Financiera de Objeto Múltiple) es una entidad financiera en México. Crece Prestaciones está registrada ante la CONDUSEF y la CNBV, lo que significa que operamos bajo el marco legal mexicano y tú cuentas con la protección de las autoridades financieras. Todos nuestros contratos son transparentes y están respaldados por la ley.',
  },
  {
    question: '¿Puedo liquidar mi crédito por adelantado sin penalización?',
    answer: 'Sí. En Crece Prestaciones no cobramos penalización por pagos anticipados ni por liquidación total antes del plazo. Fomentamos la salud financiera de nuestros clientes.',
  },
  {
    question: '¿Ofrecen créditos para personas sin historial crediticio?',
    answer: 'Evaluamos cada caso de manera integral, no solo basándonos en el buró de crédito. Consideramos tus ingresos actuales, estabilidad laboral y capacidad de pago. Sabemos que muchas personas responsables no tienen historial crediticio y merecen una oportunidad.',
  },
  {
    question: '¿Cómo funciona el programa de distribuidores?',
    answer: 'El programa de distribuidores te permite iniciar tu propio negocio de distribución de vales de dinero con el respaldo de Crece Prestaciones. Recibes una línea de crédito revolvente para otorgar prestamos, capacitación inicial, material de apoyo y comisiones atractivas. No necesitas inversión inicial. Contáctanos para conocer los detalles completos.',
  },
  {
    question: '¿Qué garantías o avales necesito para un crédito PyME?',
    answer: 'Para créditos PyME evaluamos el historial y flujo de tu negocio. En la mayoría de los casos requerimos garantía hipotecaria. Trabajamos contigo para encontrar la mejor estructura de garantía según tu situación. Nuestro objetivo es facilitar el acceso al crédito, no complicarlo.',
  },
  {
    question: '¿Mis datos personales están seguros con ustedes?',
    answer: 'Absolutamente. Toda tu información está protegida bajo nuestro Aviso de Privacidad y cumplimos con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares. Nunca compartimos tu información con terceros sin tu consentimiento expreso.',
  },
];

function FAQAccordion({ item, isOpen, onToggle, index }: { item: FAQItem; isOpen: boolean; onToggle: () => void; index: number }) {
  return (
    <div
      className={`rounded-3xl overflow-hidden transition-all duration-500 ${
        isOpen ? '' : ''
      }`}
      style={{
        background: isOpen ? 'oklch(var(--background-50))' : 'oklch(var(--background-100))',
        border: isOpen ? '1px solid oklch(var(--accent-500) / 0.15)' : '1px solid transparent',
      }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-6 md:p-7 text-left cursor-pointer group"
      >
        <div className="flex items-center gap-5 min-w-0">
          <span
            className={`w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-500 ${
              isOpen ? 'bg-accent-500 text-white scale-110' : 'bg-background-200 text-foreground-350 group-hover:bg-background-300'
            }`}
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            className={`text-base md:text-lg font-semibold transition-colors duration-300 truncate ${
              isOpen ? 'text-foreground-950' : 'text-foreground-650 group-hover:text-foreground-900'
            }`}
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {item.question}
          </span>
        </div>
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
            isOpen ? 'bg-accent-100 rotate-45' : 'bg-background-200 group-hover:bg-background-300'
          }`}
        >
          <i className={`ri-add-line text-base ${isOpen ? 'text-accent-600' : 'text-foreground-350'}`}></i>
        </div>
      </button>

      <div
        className="overflow-hidden transition-all duration-500 ease-out"
        style={{
          maxHeight: isOpen ? '400px' : '0px',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className="px-6 md:px-7 pb-6 md:pb-7 pl-[84px] md:pl-[88px]">
          <p className="text-foreground-500 text-sm leading-relaxed">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="relative py-20 md:py-28 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, oklch(var(--background-50)) 0%, oklch(var(--background-100)) 100%)',
      }}
    >
      <svg className="absolute top-0 left-0 w-full h-64 opacity-[0.025] pointer-events-none" viewBox="0 0 1440 200">
        <path d="M0 100 Q360 20 720 100 T1440 100" fill="none" stroke="oklch(var(--accent-500))" strokeWidth="0.5" />
        <path d="M0 130 Q360 50 720 130 T1440 130" fill="none" stroke="oklch(var(--primary-500))" strokeWidth="0.3" />
      </svg>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <ScrollReveal animation="fade-up" duration={600} className="lg:w-[400px] flex-shrink-0">
            <div className="lg:sticky lg:top-28">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">FAQ</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Preguntas{' '}
                <span className="relative">
                  <span className="relative z-10 bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                    Frecuentes
                  </span>
                </span>
              </h2>
              <p className="text-foreground-500 text-sm leading-relaxed">
                Respuestas directas a las dudas más comunes. Si no encuentras lo que buscas, nuestro equipo está listo para ayudarte.
              </p>


            </div>
          </ScrollReveal>

          <div className="flex-1 flex flex-col gap-3">
            <ScrollReveal animation="fade-up" duration={600} delay={100}>
              <div className="flex flex-col gap-3">
                {faqItems.map((item, index) => (
                  <FAQAccordion
                    key={index}
                    item={item}
                    index={index}
                    isOpen={openIndex === index}
                    onToggle={() => handleToggle(index)}
                  />
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}